import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2, UserPlus } from 'lucide-react';
import { cn } from '../../../../lib/cn';
import { buscarPersonasIncorp } from '../api';

/**
 * PersonaPickerMiembro — autocomplete AÑADIR-UNO-A-UNO de personas INTERNAS para los miembros de un
 * área (§3.5). Adaptación del PersonaPicker de Soporte (decisión §3.1): apunta al endpoint incorp
 * (`GET /api/incorporaciones/personas/buscar`, solo internos, permiso .configurar) y en vez de
 * mantener un chip de selección única, dispara `onAdd(persona)` y se limpia para el siguiente.
 *
 * - Debounce 300ms; mínimo 2 caracteres.
 * - Excluye del desplegable a quien ya es miembro (`excluirIds`).
 * - Cierra al click fuera y con Escape. NO busca al montar.
 *
 * props: onAdd(persona) · excluirIds:number[] · disabled
 *
 * GENERALIZACIÓN ADITIVA (Fase 11 §4.5). Tres props opcionales, y el comportamiento sin ellas es
 * exactamente el de antes:
 *   · `buscar(q)`    → de dónde salen los resultados. Por defecto, el endpoint de Incorporaciones
 *                      (sólo internos). Bajas pasa el suyo, que cubre los DOS ejes.
 *   · `placeholder`  → "Añadir persona…" no vale cuando lo que se hace es elegir a quién dar de baja.
 *   · `renderExtra(p)` → una marca por fila (Bajas avisa de quién ya tiene un proceso abierto).
 *   · `enFlujo`      → los resultados se pintan EN EL FLUJO, no flotando sobre la página.

 * POR QUÉ `enFlujo` (Fase 11 §2.ter, corrección 4). El desplegable normal es `absolute`, y
 * dentro de un modal eso NO PUEDE funcionar: el panel del modal lleva `overflow-hidden` y su
 * cuerpo `overflow-y-auto`, así que cualquier hijo posicionado por encima del borde se recorta.
 * No es un problema de `z-index` —subirlo no sirve de nada contra un `overflow`—, es que la
 * lista está en el contenedor equivocado.
 *
 * La salida no es sacar la lista del modal con un portal, que traería su propio recálculo de
 * posición al hacer scroll, sino **dejar de flotar**: en flujo, la lista empuja el contenido y
 * el propio cuerpo del modal hace scroll si hace falta. Menos código y ningún caso raro.
 *
 * Se generaliza este componente en vez de escribir un segundo picker porque lo reutilizable es la
 * INTERACCIÓN —debounce, cierre al click fuera, Escape, mínimo de caracteres, estados de vacío—,
 * que es justo la parte que se copia mal. La fuente de datos es el parámetro, no el componente.
 */
const MIN_CHARS = 2;

export default function PersonaPickerMiembro({
  onAdd, excluirIds = [], disabled = false,
  buscar, placeholder = 'Añadir persona: nombre, apellidos o email…', renderExtra, enFlujo = false,
}) {
  const [q, setQ] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [buscado, setBuscado] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const t = q.trim();
    if (t.length < MIN_CHARS) {
      setResultados([]); setCargando(false); setBuscado(false);
      return undefined;
    }
    setCargando(true);
    const handle = setTimeout(async () => {
      try {
        const data = buscar ? await buscar(t) : await buscarPersonasIncorp(t);
        setResultados(Array.isArray(data?.personas) ? data.personas : []);
      } catch {
        setResultados([]);
      } finally {
        setCargando(false);
        setBuscado(true);
        setAbierto(true);
      }
    }, 300);
    return () => clearTimeout(handle);
    // `buscar` entra en las dependencias: en Bajas la función cambia al cambiar de eje, y
    // sin esto el desplegable seguiría buscando en el anterior sin que nada lo delatase.
  }, [q, buscar]);

  useEffect(() => {
    if (!abierto) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [abierto]);

  const anadir = (p) => {
    onAdd?.(p);
    setQ(''); setResultados([]); setAbierto(false); setBuscado(false);
  };

  const q2 = q.trim();
  const hint1char = q2.length === 1;
  const excluidos = new Set(excluirIds);
  const visibles = resultados.filter((p) => !excluidos.has(p.id));
  const mostrarDropdown = abierto && q2.length >= MIN_CHARS;

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-v25-ink-400" />
        <input
          type="text"
          value={q}
          disabled={disabled}
          onChange={(e) => { setQ(e.target.value); setAbierto(true); }}
          onFocus={() => { if (q2.length >= MIN_CHARS) setAbierto(true); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setAbierto(false); }}
          placeholder={placeholder}
          className={cn(
            'w-full rounded-v25-md border border-v25-line bg-white py-2 pl-8 pr-3 text-sm',
            'focus:border-v25-focus focus:shadow-v25-focus focus:outline-none',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        />
      </div>

      {hint1char && <p className="mt-1 text-xs text-v25-ink-400">Escribe al menos 2 caracteres.</p>}

      {mostrarDropdown && (
        <div className={cn(
          'mt-1 w-full overflow-y-auto rounded-v25-md border border-v25-line bg-white',
          enFlujo ? 'relative max-h-[240px]' : 'absolute z-50 max-h-[280px] shadow-lg',
        )}>
          {cargando ? (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-v25-ink-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Buscando…
            </div>
          ) : visibles.length === 0 ? (
            buscado ? (
              <div className="px-3 py-4 text-sm text-v25-ink-400">
                {resultados.length > 0 && excluirIds.length > 0
                  ? 'Todas las coincidencias ya están en la lista.'
                  : 'No se han encontrado personas con ese texto.'}
              </div>
            ) : null
          ) : (
            <ul className="divide-y divide-v25-line">
              {visibles.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => anadir(p)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-v25-surface-sunken"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm text-v25-ink-800">{p.nombre}</div>
                      <div className="truncate text-xs text-v25-ink-500">{p.email || '—'}</div>
                    </div>
                    {renderExtra ? renderExtra(p) : <UserPlus className="h-4 w-4 shrink-0 text-v25-ink-400" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
